import BigNumber from 'bignumber.js'
import FormValidator from 'config/constants/formValidator'
import { ChainIdEnum } from 'config/constants/network'
import { BIG_ZERO } from 'config/constants/number'
import fiatTokens from 'config/constants/tokens/fiat'
import { Network, Token } from 'config/types'
import { WageringConditionBonus } from 'config/types/bonus/userBonus'
import { DepositFiatPaymentMethod } from 'config/types/payment'
import { ValidationError } from 'config/types/validator'
import { WalletType } from 'config/types/wallet'
import { useAnalytics } from 'hooks/useAnalytics'
import useApproveDepositContract from 'hooks/useApproveDepositContract'
import useDebounceCallback from 'hooks/useDebounceCallback'
import useExecuteDeposit from 'hooks/useDeposit'
import useDepositFee from 'hooks/useDepositFee'
import useDepositTokenAllowance from 'hooks/useDepositTokenAllowance'
import useForm from 'hooks/useForm'
import { useIsomorphicEffect } from 'hooks/useIsomorphicEffect'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import useModal from 'hooks/useModal'
import { useRequest } from 'hooks/useRequest'
import { useIsTelegramOrigin } from 'hooks/useTelegram'
import useWalletAccount from 'hooks/useWalletAccount'
import useWeb3React from 'packages/wagmi/hooks/useWeb3React'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PaymentService from 'services/PaymentService'
import { useAppSelector } from 'state'
import { useTokenUsdPrice } from 'state/app/hooks'
import { useWalletInfo } from 'state/auth/hooks'
import { useTokenWalletBalance } from 'state/profile/hooks'
import { formatToApiNetworkField } from 'utils'
import { getDecimalAmount, getFullDisplayBalance } from 'utils/formatBalance'
import { isDisableSubscribeDeposit, isHPNNetwork, isSolChain, isTonChain, isXrpChain } from 'utils/network'
import { HunnyToast } from 'utils/toastify'
import { isReject } from 'utils/wallet'
import DepositInProgressModal from 'views/Payment/components/Deposit/DepositByFiat/DepositInProgressModal'
import PaymentMethodBlockingModal from 'views/Payment/components/Deposit/DepositByFiat/PaymentMethodBlockingModal'
import { useSwitchNetwork } from 'wagmi'

type IUseDepositBaseControlProps = {
  mode: DepositModeEnums
  onStart?: () => void
  onSucceed?: (txnHash: string) => void
  onFailed?: (error?: string) => void
  selectedToken: Token
  selectedBonus?: WageringConditionBonus
  disabled?: boolean
}

type IUseDepositWalletControlProps = IUseDepositBaseControlProps & {
  mode: DepositModeEnums.WALLET
  selectedNetwork: Network
  isDepositing?: boolean
}

type IUseDepositQrControlProps = IUseDepositBaseControlProps & {
  mode: DepositModeEnums.QR
  selectedNetwork: Network
}

type IUseDepositFiatControlProps = IUseDepositBaseControlProps & {
  mode: DepositModeEnums.FIAT
}

type UseDepositControlProps = IUseDepositWalletControlProps | IUseDepositQrControlProps | IUseDepositFiatControlProps

export type DepositWalletControls = ReturnType<typeof useDepositCryptoByWallet>
export type DepositQrCodeControls = ReturnType<typeof useDepositCryptoByQRCode>
export type DepositFiatControls = ReturnType<typeof useDepositFiat>

const FIAT_MIN_MAX_DEFAULT = {
  [fiatTokens.IDR.code]: {
    min: 50000,
    max: 200000000,
  },
  [fiatTokens.PHP.code]: {
    min: 100,
    max: 1000000,
  },
  [fiatTokens.MYR.code]: {
    min: 10,
    max: 50000,
  },
  [fiatTokens.THB.code]: {
    min: 100,
    max: 500000,
  },
}

export enum DepositModeEnums {
  WALLET = 'wallet',
  QR = 'qr',
  FIAT = 'fiat',
}

export const useVerifyAndSwitchNetwork = (selectedToken: Token) => {
  const { switchNetworkAsync } = useSwitchNetwork()
  const [isSwichingNetwork, setIsSwichingNetwork] = useState<boolean>(false)
  const { recordEvent } = useAnalytics()

  const handleSwitchNetwork = useCallback(async () => {
    setIsSwichingNetwork(true)
    if (selectedToken?.network)
      await switchNetworkAsync(selectedToken?.network)
        .then(() => {
          recordEvent('deposit-switch-network-success', {})
        })
        .catch((e) => {
          if (isReject(e)) {
            recordEvent('deposit-switch-network-reject', {})
          } else {
            recordEvent('deposit-switch-network-failure', {})
          }
        })
    setIsSwichingNetwork(false)
  }, [selectedToken?.network, switchNetworkAsync])

  return useMemo(() => {
    return {
      handleSwitchNetwork,
      isSwichingNetwork,
    }
  }, [isSwichingNetwork, handleSwitchNetwork])
}

const useDepositFiatMethods = ({
  selectedCurrency,
  onCallbackResponse,
}: {
  selectedCurrency: Token
  onCallbackResponse?: (response: [string, DepositFiatPaymentMethod[]][]) => void
}) => {
  const [methods, setMethods] = useState<[string, DepositFiatPaymentMethod[]][]>([])
  const [isFetching, setIsFetcing] = useState(false)
  const { execute } = useRequest()

  useEffect(() => {
    const fetch = async () => {
      try {
        setIsFetcing(true)
        const response = await execute(PaymentService.getDepositFiatBankMethodList({ currency: selectedCurrency.code }))

        if (response?.data) {
          setMethods(response.data)
        }
        if (onCallbackResponse) {
          onCallbackResponse(response?.data)
        }
      } finally {
        setIsFetcing(false)
      }
    }

    if (selectedCurrency) {
      fetch()
    }
  }, [selectedCurrency])

  return {
    methods,
    isFetching,
  }
}

const useDepositFiat = ({ selectedCurrency }: UseDepositFiatProps) => {
  const { execute } = useRequest()
  const { t } = useTranslation()
  const [isFetchingMerchantSiteUrl, setIsFetchingMerchantSiteUrl] = useState(false)
  const { isMobile } = useMatchBreakpoints()

  const isOriginTelegram = useIsTelegramOrigin()
  const isEnableFormSubmit = useMemo(() => isOriginTelegram || isMobile, [isOriginTelegram, isMobile])
  const refForm = useRef<HTMLDivElement>(null)

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<DepositFiatPaymentMethod>(null)

  const [onPresentDepositFiatInProgressModal] = useModal(DepositInProgressModal)
  const [onPresentPaymentMethodBlockingModal] = useModal(PaymentMethodBlockingModal)
  const { isFetching: isFetchingFiatMethods, methods: paymentMethods } = useDepositFiatMethods({
    selectedCurrency,
    onCallbackResponse: () => {
      setSelectedPaymentMethod(null)
    },
  })

  const returnedForm = useForm({
    amount: {
      value: '',
      validators: selectedPaymentMethod
        ? [
            FormValidator.required,
            FormValidator.min(selectedPaymentMethod?.minAmount),
            FormValidator.max(selectedPaymentMethod?.maxAmount),
          ]
        : undefined,
    },
  })

  useEffect(() => {
    return () => {
      if (returnedForm) {
        setSelectedPaymentMethod(null)
        returnedForm.controls.amount.clear()
      }
    }
  }, [selectedCurrency])

  const formatErrors = (errors: ValidationError[]) => {
    if (errors[0] === ValidationError.NotEnough || errors[0] === ValidationError.Insufficient) {
      return t('Deposit amount has to be between {{ minAmount }} {{ currency }} and {{ maxAmount }} {{ currency }}', {
        minAmount: getFullDisplayBalance(selectedPaymentMethod?.minAmount, 0, 5),
        maxAmount: getFullDisplayBalance(selectedPaymentMethod?.maxAmount, 0, 5),
        currency: selectedPaymentMethod?.currency,
      })
    }
    if (errors[0] === ValidationError.Required) {
      return t('Deposit amount is required')
    }
  }

  const handleDeposit = useCallback(
    async (amount: number) => {
      setIsFetchingMerchantSiteUrl(true)
      const response = await PaymentService.depositByFiat({
        amount,
        bankCode: selectedPaymentMethod.code,
        currency: selectedCurrency.code,
      })

      if (response?.data) {
        if (!response.data.content && response.data.pendingTransaction) {
          onPresentPaymentMethodBlockingModal({
            paymentFiatTransaction: {
              ...response.data.pendingTransaction,
              fiat: { bankName: selectedPaymentMethod.name },
            },
          })
          setIsFetchingMerchantSiteUrl(false)
          return
        }

        const depositDetailResponse = await execute(PaymentService.getDepositByTxnHash(response.data.txnHash))

        onPresentDepositFiatInProgressModal({
          paymentFiatTransaction: {
            amount: {
              amount: amount.toString(),
              token: selectedCurrency,
            },
            createdAt: depositDetailResponse.data.createTime,
            txnHash: depositDetailResponse.data.txnHash,
            method: selectedPaymentMethod.type,
            name: selectedPaymentMethod.name,
          },
          transactionDetail: depositDetailResponse.data,
        })

        if (isEnableFormSubmit && refForm.current) {
          refForm.current.innerHTML = response.data.content

          const form = refForm.current.querySelector('form')
          if (form?.submit) {
            form.submit()
          }
        } else {
          const newWindow = (window as any)?.open()
          if (newWindow) {
            newWindow.document.write(response.data.content)
            newWindow.document.close()
          }
        }
      }

      setIsFetchingMerchantSiteUrl(false)
    },
    [selectedPaymentMethod, selectedCurrency, isEnableFormSubmit],
  )

  const fiatMinMaxDeposit = useMemo(() => {
    if (!selectedPaymentMethod || !selectedCurrency) {
      return FIAT_MIN_MAX_DEFAULT[selectedCurrency?.code] || undefined
    }

    return {
      min: selectedPaymentMethod.minAmount,
      max: selectedPaymentMethod.maxAmount,
    }
  }, [selectedPaymentMethod, selectedCurrency])

  return useMemo(() => {
    return {
      handler: {
        onDeposit: handleDeposit,
        onSelectMethod: setSelectedPaymentMethod,
        formatErrors,
      },
      status: {
        isFetchingFiatMethods,
        isFetchingMerchantSiteUrl,
      },
      data: {
        paymentMethods,
        selectedPaymentMethod,
        fiatMinMaxDeposit,
        mobileRefForm: refForm,
        enableFormSubmit: isEnableFormSubmit,
        returnedForm: {
          errorsMessage: {
            amount: formatErrors(returnedForm.states.amount.errors),
          },
          ...returnedForm,
        },
      },
    }
  }, [
    isFetchingFiatMethods,
    isFetchingMerchantSiteUrl,
    paymentMethods,
    handleDeposit,
    setSelectedPaymentMethod,
    selectedPaymentMethod,
    refForm,
    isEnableFormSubmit,
    returnedForm,
    fiatMinMaxDeposit,
  ])
}

const useDepositCryptoByQRCode = ({
  selectedToken: selectedTokenProps,
  selectedNetwork: selectedNetworkProps,
  disabled: disabledProps,
}: IUseDepositQrControlProps) => {
  const { selectedToken, disabled } = useMemo(() => {
    return {
      selectedNetwork: selectedNetworkProps,
      selectedToken: selectedNetworkProps && selectedTokenProps,
      disabled: disabledProps || !selectedTokenProps || !selectedNetworkProps,
    }
  }, [selectedNetworkProps, selectedTokenProps, disabledProps])

  const tokenInfoes = useAppSelector((state) => state.app.tokenInfoes)

  const [address, setAddress] = useState<string>('')
  const [minDepositAmount, setMinDepositAmount] = useState<string>('')
  const [isFetchingDepositInfo, setIsFetchingDepositInfo] = useState<boolean>(false)
  const debounce = useDebounceCallback()
  const { execute } = useRequest()

  useEffect(() => {
    if (disabled) return

    if (!isDisableSubscribeDeposit(selectedToken?.network)) {
      debounce(() => PaymentService.subscribeDeposit(selectedToken), 10000)
    }

    if (selectedToken) {
      const fetchDepositAddress = async () => {
        setIsFetchingDepositInfo(true)

        const response = await execute(
          PaymentService.traditionalDeposit({
            currency: selectedToken.code,
            network: formatToApiNetworkField(ChainIdEnum[selectedToken.network]),
          }),
        )

        if (response) {
          setAddress(response.data?.address)
          setMinDepositAmount(response.data?.minAmount)
        }
        setIsFetchingDepositInfo(false)
      }

      if (!isHPNNetwork(selectedToken.network)) {
        fetchDepositAddress()
      }
    }
  }, [selectedToken])

  const [addressReceiveFund, qrCode, memo] = useMemo(() => {
    if (!selectedToken || disabled) return ['', '', '']

    if (isTonChain(selectedToken.network) || isXrpChain(selectedToken.network)) {
      const spender = tokenInfoes.find(
        (tokenInfo) => tokenInfo.network === selectedToken.network && tokenInfo.token === selectedToken.code,
      )?.contractAddress

      return [spender, spender, address]
    }

    return [address, address, '']
  }, [selectedToken, address, tokenInfoes, disabled])

  return useMemo(
    () => ({
      data: {
        addressReceiveFund,
        qrCode,
        memo,
        minDepositAmount,
      },
      status: {
        isFetchingDepositInfo,
      },
    }),
    [addressReceiveFund, qrCode, memo, minDepositAmount, isFetchingDepositInfo],
  )
}

const useDepositCryptoByWallet = ({
  onStart,
  onSucceed,
  onFailed,
  selectedToken: selectedTokenProps,
  selectedNetwork: selectedNetworkProps,
  selectedBonus,
  isDepositing,
  disabled: disabledProps,
}: IUseDepositWalletControlProps) => {
  const { selectedToken, disabled } = useMemo(() => {
    return {
      selectedNetwork: selectedNetworkProps,
      selectedToken:
        selectedNetworkProps && selectedNetworkProps.chainId === selectedTokenProps.network && selectedTokenProps,
      disabled: disabledProps || !selectedTokenProps || !selectedNetworkProps,
    }
  }, [selectedNetworkProps, selectedTokenProps, disabledProps])

  const { walletInfo, isWrongAddress, wallet } = useWalletInfo()

  const { t } = useTranslation()
  const accounts = useWalletAccount()
  const { chainId } = useWeb3React()

  const isWrongChain = useMemo(
    () => walletInfo.type !== WalletType.SOL && chainId != selectedToken?.network,
    [walletInfo.type, chainId, selectedToken?.network],
  )
  const account = accounts[wallet?.type]
  const { approve, approveProcess } = useApproveDepositContract(account ? selectedToken : null)
  const { allowance, refresh } = useDepositTokenAllowance(!isWrongChain && !isWrongAddress ? selectedToken : null)

  const { deposit, submiting } = useExecuteDeposit(selectedToken)
  const { handleSwitchNetwork, isSwichingNetwork } = useVerifyAndSwitchNetwork(selectedToken)

  const estimatedFee = useDepositFee(selectedToken)
  const tokenAmount = useTokenWalletBalance(selectedToken, wallet?.address)
  const walletBalanceLockDisplayRef = useRef(null)
  const priceInUsd = useTokenUsdPrice(selectedToken)

  useIsomorphicEffect(() => {
    if (isDepositing) {
      walletBalanceLockDisplayRef.current = tokenAmount
    }
  }, [isDepositing])

  const selectedTokenAmount = isDepositing ? walletBalanceLockDisplayRef.current : tokenAmount
  const balance = useMemo(
    () => (selectedTokenAmount ? (selectedTokenAmount.amount as BigNumber) : BIG_ZERO),
    [selectedTokenAmount?.amount?.toString(), isDepositing],
  )

  const returnedForm = useForm({
    amount: {
      validators: [FormValidator.lte(0), FormValidator.max(selectedTokenAmount?.amount)],
      value: '',
    },
  })

  useEffect(() => {
    if (returnedForm.states.amount.isDirty) {
      returnedForm.validate('amount')
    }
  }, [returnedForm.states.amount.isDirty])

  const isTokenApproved = useMemo(() => {
    if (isSolChain(selectedToken?.network)) return true

    return returnedForm.states.amount.value
      ? allowance?.gte(getDecimalAmount(returnedForm.states.amount.value, selectedToken?.decimals))
      : allowance?.gt(0)
  }, [allowance, returnedForm.states?.amount?.value, selectedToken?.decimals])

  const handleApprove = useCallback(async () => {
    if (disabled) return

    const hash = await approve()
    if (hash) {
      refresh()
      HunnyToast.success(t('Approve {{token}} successfully!', { token: selectedToken?.name }))
    }
  }, [approve, refresh, selectedToken?.name, t, disabled])

  const handleDeposit = useCallback(async () => {
    const isValid = await returnedForm.validateAll()
    if (!isValid || disabled) {
      return
    }

    if (
      selectedBonus.id !== 0 &&
      selectedBonus.minDepositBigAmount.gt(priceInUsd.multipliedBy(returnedForm.states.amount.value))
    ) {
      HunnyToast.error(
        t(
          'The min deposit amount to activate your selected bonus is {{value}} {{token}}. Please choose a higher amount or select "Without Bonus".',
          {
            value: selectedBonus.minDepositBigAmount.dividedBy(priceInUsd).decimalPlaces(6, BigNumber.ROUND_CEIL),
            token: selectedToken.name,
          },
        ),
      )
      return
    }

    try {
      if (onStart) onStart()

      const hash = await deposit(returnedForm.states.amount.value)

      if (hash) {
        returnedForm.controls.amount.onValueChanged('')
        if (onSucceed) onSucceed(hash)
      } else if (onFailed) {
        onFailed()
      }
    } catch (error: any) {
      if (onFailed) {
        onFailed(error?.message)
      }
    }
  }, [
    returnedForm,
    deposit,
    returnedForm.states.amount.value,
    onSucceed,
    onFailed,
    selectedBonus,
    priceInUsd,
    disabled,
  ])

  return {
    handler: {
      handleDeposit,
      handleApprove,
      handleSwitchNetwork,
    },
    status: {
      tokenApproveProcessStatus: approveProcess,
      isTokenDepositInSubmitting: submiting,
      isNetworkSwitching: isSwichingNetwork,
      addressApproved: !isWrongAddress && account,
      tokenApproved: isTokenApproved,
      evmChainApproved: !isWrongChain,
    },
    data: {
      allowance,
      connectedAccount: account,
      returnedForm,
      selectedTokenAmount,
      estimatedFee,
      balance,
      tokenAmount,
    },
  }
}

export const useDepositControl = <T>(props: UseDepositControlProps) => {
  switch (props.mode) {
    case DepositModeEnums.WALLET:
      const depositWalletControls = useDepositCryptoByWallet({ ...props })
      return {
        mode: DepositModeEnums.WALLET,
        depositControl: depositWalletControls as T,
      }
    case DepositModeEnums.QR:
      const depositQrCodeControls = useDepositCryptoByQRCode({ ...props })
      return {
        mode: DepositModeEnums.QR,
        depositControl: depositQrCodeControls as T,
      }
    case DepositModeEnums.FIAT:
      const depositFiatControls = useDepositFiat({ selectedCurrency: props.selectedToken })
      return { mode: DepositModeEnums.FIAT, depositControl: depositFiatControls as T }
    default:
      throw new Error('Invalid deposit mode')
  }
}

type UseDepositFiatProps = {
  selectedCurrency: Token
}
